/**
 * Contact Store
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { contactsApi } from '../api/contactsApi';
import type {
    Contact,
    ContactListParams,
    CreateIndividualData,
    CreateIndividualPayload,
    CreateCorporateData,
    UpdateContactData
} from '../types/contact.types';

// CATEGORY_CD 값 상수
const CATEGORY_INDIVIDUAL = 'B09002'; // Individual
const CATEGORY_CORPORATE = 'B09001'; // Corporate

export const useContactStore = defineStore('contacts', () => {
    // State
    const contacts = ref<Contact[]>([]);
    const currentContact = ref<Contact | null>(null);
    const total = ref(0);
    const isLoading = ref(false);
    const error = ref<string | null>(null);

    const normalizeCategoryCd = (contact: Contact | null | undefined): string => {
        const raw =
            contact?.CATEGORY_CD ??
            (contact as any)?.category_cd ??
            (contact as any)?.categoryCd ??
            '';
        return String(raw).trim().toUpperCase();
    };

    // Getters
    // CATEGORY_CD: 'B09002' => Individual, 'B09001' => Corporate
    const isIndividual = computed(
        () => normalizeCategoryCd(currentContact.value) === CATEGORY_INDIVIDUAL,
    );

    const isCorporate = computed(
        () => normalizeCategoryCd(currentContact.value) === CATEGORY_CORPORATE,
    );

    // Actions
    const fetchContacts = async (params?: ContactListParams) => {
        isLoading.value = true;
        error.value = null;
        try {
            console.log('Fetching contacts with params:', params);
            const response = await contactsApi.getContacts(params);
            console.log('API Response:', response);
            
            // 백엔드 응답 구조: { success: true, contacts: [...], total: 10, page: 1, limit: 10 }
            // apiClient.get은 response.data를 반환하므로, 실제 응답은 이미 파싱된 객체
            // sendSuccessResponse가 반환하는 구조: { success: true, contacts, total, ... }
            const responseData = response as any;
            
            contacts.value = responseData.contacts || [];
            total.value = responseData.total || 0;
            
            console.log('Contacts set:', contacts.value.length, 'Total:', total.value);
        } catch (err: any) {
            console.error('Error fetching contacts:', err);
            console.error('Error response:', err.response);
            console.error('Error data:', err.response?.data);
            
            // 백엔드 에러 응답 구조에 맞게 에러 메시지 추출
            const errorMessage = err.response?.data?.error?.message 
                || err.response?.data?.message 
                || err.message 
                || 'Contact 목록을 불러오는데 실패했습니다.';
            
            error.value = errorMessage;
            throw new Error(errorMessage);
        } finally {
            isLoading.value = false;
        }
    };

    const fetchContactById = async (id: string, options?: { entityCd?: string }) => {
        isLoading.value = true;
        error.value = null;
        try {
            console.log('Fetching contact by id:', id, options);
            const response = await contactsApi.getContactById(
                id,
                options?.entityCd ? { entityCd: options.entityCd } : undefined,
            );
            console.log('Contact API Response:', response);
            
            // 백엔드 응답 구조: { success: true, contact: {...} }
            // apiClient.get은 response.data를 반환하므로, 실제 응답은 이미 파싱된 객체
            const responseData = response as any;
            const contact =
                responseData?.contact ??
                responseData?.data?.contact ??
                (responseData?.success && responseData?.CONTACT_CD ? responseData : null) ??
                null;

            currentContact.value = contact;
            if (!contact) {
                console.warn('[Contact Store] GET detail: contact missing in response', responseData);
            }
            return currentContact.value;
        } catch (err: any) {
            console.error('Error fetching contact:', err);
            console.error('Error response:', err.response);
            console.error('Error data:', err.response?.data);
            
            error.value = err.response?.data?.error?.message 
                || err.response?.data?.message 
                || err.message 
                || 'Contact 정보를 불러오는데 실패했습니다.';
            throw new Error(error.value || 'Contact 정보를 불러오는데 실패했습니다.');
        } finally {
            isLoading.value = false;
        }
    };

    const createIndividual = async (data: CreateIndividualPayload | CreateIndividualData) => {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await contactsApi.createIndividual(data);
            await fetchContacts(); // 목록 새로고침
            return response.data?.contactCd;
        } catch (err: any) {
            error.value = err.message || 'Individual Contact 생성에 실패했습니다.';
            throw err;
        } finally {
            isLoading.value = false;
        }
    };

    const createCorporate = async (data: CreateCorporateData) => {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await contactsApi.createCorporate(data);
            await fetchContacts(); // 목록 새로고침
            return response.data?.contactCd;
        } catch (err: any) {
            error.value = err.message || 'Corporate Contact 생성에 실패했습니다.';
            throw err;
        } finally {
            isLoading.value = false;
        }
    };

    const updateContact = async (id: string, data: UpdateContactData, options?: { skipReload?: boolean }) => {
        isLoading.value = true;
        error.value = null;
        try {
            await contactsApi.updateContact(id, data);
            // 삭제 등으로 상세가 더 이상 조회되지 않아도 되는 경우를 위해 옵션 제공
            if (!options?.skipReload) {
                await fetchContactById(id); // 상세 새로고침
            }
        } catch (err: any) {
            error.value = err.message || 'Contact 수정에 실패했습니다.';
            throw err;
        } finally {
            isLoading.value = false;
        }
    };

    const deleteContact = async (id: string) => {
        isLoading.value = true;
        error.value = null;
        try {
            await contactsApi.deleteContact(id);
            await fetchContacts(); // 목록 새로고침
            if (currentContact.value?.CONTACT_CD === id) {
                currentContact.value = null;
            }
        } catch (err: any) {
            error.value = err.message || 'Contact 삭제에 실패했습니다.';
            throw err;
        } finally {
            isLoading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    return {
        // State
        contacts,
        currentContact,
        total,
        isLoading,
        error,
        // Getters
        isIndividual,
        isCorporate,
        // Actions
        fetchContacts,
        fetchContactById,
        createIndividual,
        createCorporate,
        updateContact,
        deleteContact,
        clearError
    };
});

